import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, asArray, formatApiError } from "@/lib/api";
import { pesoOr } from "@/lib/money";
import { AlertTriangle, Clock3, Timer, CheckCircle2, RefreshCw } from "lucide-react";

// Nothing is invoiceable until approved, and until this page there was no
// screen to approve in. /desk/time-integrity -- which reports any technician
// whose entries cover the same wall-clock minute twice, the one error that
// silently OVERCHARGES a client -- had no reader at all.
//
// /time/summary reports totals only (by-technician / by-client minutes, no
// entry ids), so it cannot drive an Approve button. The listing this page
// needs is GET /desk/time-entries?approved=false, added alongside this page
// for exactly that reason.
//
// The server is the gate: every write here hits require_roles(service_desk_
// manager, l3_technician, msp_owner, super_admin) -- Layout.jsx hides the nav
// entry for anyone else, but that is a door removed, not a lock installed.

const RUNAWAY_CAP_MINUTES = 8 * 60;

// The local `peso()` that used to live here is gone -- see
// `frontend/src/lib/money.js`. It read the DERIVED FLOAT (`amount`) and
// rendered it with `minimumFractionDigits: 0` and no maximum, so ₱1,234.50
// came out as "₱1,234.5" on the screen a manager approves billable time from.
// Every figure below now reads the INTEGER (`amount_minor`), which is the
// field of record; the float beside it is a display copy this page no longer
// needs.

function fmtMinutes(m) {
  const n = Number(m) || 0;
  const h = Math.floor(n / 60);
  const r = n % 60;
  if (!h) return `${r} min`;
  return `${h}h ${r}m`;
}

function fmtTime(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch { return String(ts); }
}

export default function TimeApproval() {
  const [integrity, setIntegrity] = useState(null);
  const [running, setRunning] = useState(null);
  const [entries, setEntries] = useState(null);
  const [billing, setBilling] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null); // entry id in flight

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/desk/time-integrity").catch((e) => ({ error: formatApiError(e) })),
      api.get("/timers/running").catch((e) => ({ error: formatApiError(e) })),
      api.get("/desk/time-entries?approved=false").catch((e) => ({ error: formatApiError(e) })),
      api.get("/desk/billing").catch((e) => ({ error: formatApiError(e) })),
    ]).then(([i, r, e, b]) => {
      setIntegrity(i?.data ?? i);
      setRunning(r?.data ?? r);
      setEntries(e?.data ?? e);
      setBilling(b?.data ?? b);
      // Only surface a page-level error if EVERYTHING failed -- a single
      // section failing (e.g. a role that can see running timers but not
      // billing) should degrade that section, not blank the page.
      const all = [i, r, e, b];
      setErr(all.every((x) => x?.error) ? formatApiError({ message: all[0]?.error }) : "");
    }).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (entryId) => {
    setApproving(entryId);
    try {
      await api.post(`/time/${entryId}/approve`);
      toast.success("Time entry approved");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setApproving(null);
    }
  };

  const overlaps = asArray(integrity?.detail);
  const hasOverlaps = !integrity?.error && overlaps.length > 0;
  const runningRows = asArray(running?.running);
  const awaiting = asArray(entries?.entries);

  return (
    <div className="space-y-4" data-testid="time-approval-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Service Desk · Billing</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Time Approval</h1>
          <div className="text-xs text-zinc-500 mt-1">Nothing is invoiced until it is approved here.</div>
        </div>
        <button onClick={load} data-testid="time-approval-refresh"
          className="border border-zinc-800 rounded-md px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-900 flex items-center gap-1.5">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {err && <div className="border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm">{err}</div>}
      {loading && !err && <div className="text-zinc-600 text-sm">Loading…</div>}

      {/* 1. Integrity banner. THREE states, never collapsed into two: found
          overlaps (red banner), checked and clean (the muted line below), or
          could not check at all (its own error row). Silence on failure here
          reads exactly like "no overlaps" -- and this is the one report whose
          entire job is catching the error that silently overcharges a
          client, so an unreachable check must never look like a clean one. */}
      {integrity?.error && (
        <ErrorRow testid="time-integrity-error"
          msg={`Could not check for overlapping time entries — this is NOT an all-clear. (${integrity.error})`} />
      )}
      {hasOverlaps && (
        <div className="border border-red-500/40 bg-red-950/40 rounded-md p-4 space-y-2" data-testid="time-integrity-banner">
          <div className="flex items-center gap-2 text-red-300 font-medium">
            <AlertTriangle size={16} />
            {overlaps.length} overlapping time {overlaps.length === 1 ? "entry" : "entries"} — the same wall-clock minute billed twice
          </div>
          <div className="text-xs text-red-300/80">
            Each row below covers a minute already billed by another entry from the same technician. Do not approve either entry until it is corrected.
          </div>
          <div className="space-y-1.5">
            {overlaps.map((o, idx) => (
              <div key={`${o.entry_a}-${o.entry_b}-${idx}`} data-testid={`time-integrity-row-${idx}`}
                className="text-sm text-red-200 bg-red-950/30 border border-red-900/40 rounded px-3 py-2 font-mono">
                {o.user_name || o.user_id} — ticket {o.ticket_b} overlaps ticket {o.ticket_a} from {fmtTime(o.overlap_from)} to {fmtTime(o.overlap_to)}
              </div>
            ))}
          </div>
        </div>
      )}
      {!hasOverlaps && !loading && integrity && !integrity.error && (
        <div className="text-[11px] text-zinc-600 font-mono" data-testid="time-integrity-clean">
          No overlapping time entries in the last {integrity.window_days} days.
        </div>
      )}

      {/* 2. Running timers */}
      <Section title="Running timers" icon={Timer} testid="running-timers-section">
        {running?.error && <ErrorRow msg={running.error} />}
        {!running?.error && !runningRows.length && (
          <EmptyRow msg="Nobody is on the clock right now." />
        )}
        {!running?.error && !!runningRows.length && (
          <Table
            head={["Technician", "Ticket", "Elapsed", ""]}
            rows={runningRows.map((t) => {
              const nearingCap = (t.elapsed_minutes || 0) >= RUNAWAY_CAP_MINUTES * 0.9;
              return {
                key: t.id,
                testid: `running-timer-${t.id}`,
                cells: [
                  t.user_name || t.user_id || "—",
                  <Link to={`/tickets/${t.ticket_id}`} className="hover:text-blue-400" key="ticket">
                    #{t.ticket_number ?? "—"} {t.ticket_title || ""}
                  </Link>,
                  fmtMinutes(t.elapsed_minutes),
                  nearingCap ? (
                    <span className="text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border bg-amber-500/10 text-amber-300 border-amber-500/30">
                      Nearing 8h cap
                    </span>
                  ) : null,
                ],
              };
            })}
          />
        )}
      </Section>

      {/* 3. Awaiting approval */}
      <Section title="Awaiting approval" icon={Clock3} testid="awaiting-approval-section">
        {entries?.error && <ErrorRow msg={entries.error} />}
        {!entries?.error && !awaiting.length && <EmptyRow msg="No time entries are waiting for approval." />}
        {!entries?.error && !!awaiting.length && (
          <Table
            head={["Technician", "Ticket", "Work type", "Raw", "Rounded", "Amount", ""]}
            rows={awaiting.map((e) => ({
              key: e.id,
              testid: `time-entry-${e.id}`,
              cells: [
                e.user_name || e.user_id || "—",
                <Link to={`/tickets/${e.ticket_id}`} className="hover:text-blue-400" key="ticket">
                  #{e.ticket_number ?? "—"} {e.ticket_title || ""}
                </Link>,
                <span key="wt">
                  {e.work_type_name || e.work_type || "—"}
                  {e.auto_stopped && (
                    <span className="ml-2 text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border bg-red-500/10 text-red-300 border-red-500/30">
                      Auto-stopped at 8h cap
                    </span>
                  )}
                </span>,
                // Raw AND rounded, side by side -- rounding is applied UP to the
                // increment, and a client disputing an invoice is entitled to
                // what actually happened, not just the billed figure.
                <span key="raw" className="font-mono text-zinc-500">{e.raw_minutes ?? "—"} min</span>,
                <span key="rounded" className="font-mono text-zinc-200">{e.minutes ?? "—"} min</span>,
                <span key="amt" className="font-mono">{e.billable ? pesoOr(e.amount_minor, "Not priced") : "Non-chargeable"}</span>,
                <button key="approve" onClick={() => approve(e.id)} disabled={approving === e.id}
                  data-testid={`time-approve-${e.id}`}
                  className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded px-3 py-1 text-xs flex items-center gap-1.5">
                  <CheckCircle2 size={13} /> {approving === e.id ? "Approving…" : "Approve"}
                </button>,
              ],
            }))}
          />
        )}
      </Section>

      {/* 4. Billing split */}
      <Section title="Billing split" icon={Clock3} testid="billing-split-section">
        {billing?.error && <ErrorRow msg={billing.error} />}
        {!billing?.error && billing && (
          <div className="space-y-3">
            {/* ⚠️ **EVERY FIGURE BELOW IS SHORT BY THESE ROWS, AND UNTIL NOW
                NOTHING ON ANY SCREEN SAID SO.** `/api/desk/billing` has
                returned `unpriced_entries` since Task 2 -- per bucket, per
                client and for the window -- precisely so a screen could name
                it, and zero screens read it. On production today 306 approved
                billable entries carry no `amount_minor`, so these tiles and
                every client row read P0.00 with the count that explains them
                sitting unrendered in the same payload. That is the
                zero-that-scans-like-any-other-zero defect, one screen over
                from the billing run list where it was just fixed. */}
            {billing.unsummable_entries > 0 && (
              <div className="border border-red-500/40 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm space-y-1"
                data-testid="billing-unsummable-banner">
                <div className="font-medium">
                  {billing.unsummable_entries} {billing.unsummable_entries === 1 ? "entry carries" : "entries carry"} an amount that is not a whole number of centavos
                </div>
                <div className="text-[11px] text-red-300/80 leading-relaxed">
                  No total on this page includes {billing.unsummable_entries === 1 ? "it" : "them"} and no billing run will claim {billing.unsummable_entries === 1 ? "it" : "them"}.
                  Until 2026-09-25 a single one of these made this whole section fail to load rather than report itself. Correct the stored amount on the entry.
                </div>
              </div>
            )}
            {billing.unpriced_entries > 0 && (
              <div className="border border-amber-500/40 bg-amber-950/20 text-amber-300 rounded px-4 py-3 text-sm space-y-1"
                data-testid="billing-unpriced-banner">
                <div className="font-medium">
                  {billing.unpriced_entries} {billing.unpriced_entries === 1 ? "entry carries" : "entries carry"} no amount, so every figure below is short by {billing.unpriced_entries === 1 ? "it" : "them"}
                </div>
                <div className="text-[11px] text-amber-300/80 leading-relaxed">
                  These are not zero-value entries, they are entries with no recorded amount at all: no total on this page includes them and no billing run can claim them.
                  A client row reading zero may be a client with nothing chargeable, or a client whose whole month is in this count. The per-client counts below say which.
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Stat label="Billable" bucket={billing.billable} />
              <Stat label="Non-billable" bucket={billing.non_billable} />
              <Stat label="Awaiting approval" bucket={billing.awaiting_approval} tone="amber" />
              <Stat label="Approved, not invoiced" bucket={billing.approved_not_invoiced} tone="blue" />
            </div>
            <div className="text-xs text-zinc-500">
              Window: {billing.window_days} days · rounded to {billing.increment_minutes}-minute increments
              {billing.flagged_auto_stopped ? ` · ${billing.flagged_auto_stopped} auto-stopped at the 8h cap` : ""}
              {billing.overlapping_entries ? ` · ${billing.overlapping_entries} overlapping (see banner above)` : ""}
            </div>

            <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
              <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-zinc-500 font-mono border-b border-zinc-900">
                By client (billable only)
              </div>
              <table className="w-full text-sm hidden sm:table">
                <thead className="bg-zinc-900/50 border-b border-zinc-900 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                  <tr>
                    <th className="text-left px-4 py-2">Client</th>
                    <th className="text-left px-4 py-2">Entries</th>
                    <th className="text-left px-4 py-2">Hours</th>
                    <th className="text-left px-4 py-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {asArray(billing.by_client).map((c) => (
                    <tr key={c.client_id} className="border-b border-zinc-900" data-testid={`billing-client-${c.client_id}`}>
                      <td className="px-4 py-2 text-zinc-200">{c.name || c.client_id}</td>
                      <td className="px-4 py-2 font-mono text-zinc-400">
                        {c.entries}
                        {c.unpriced_entries > 0 && (
                          <span className="block text-[10px] text-amber-300" data-testid={`billing-client-unpriced-${c.client_id}`}>
                            {c.unpriced_entries} with no amount
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 font-mono text-zinc-400">{c.hours}</td>
                      <td className="px-4 py-2 font-mono text-zinc-100">{pesoOr(c.amount_minor, "Not priced")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="sm:hidden divide-y divide-zinc-900">
                {asArray(billing.by_client).map((c) => (
                  <div key={c.client_id} className="p-4 space-y-1" data-testid={`billing-client-card-${c.client_id}`}>
                    <div className="text-zinc-100 font-medium">{c.name || c.client_id}</div>
                    <div className="text-xs text-zinc-500">
                      {c.entries} entries · {c.hours}h
                      {c.unpriced_entries > 0 && (
                        <span className="text-amber-300"> · {c.unpriced_entries} with no amount</span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-300 font-mono">{pesoOr(c.amount_minor, "Not priced")}</div>
                  </div>
                ))}
              </div>
              {!asArray(billing.by_client).length && (
                <div className="px-4 py-6 text-zinc-600 text-sm">No billable time in this window.</div>
              )}
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}

function Section({ title, icon: Icon, testid, children }) {
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-center gap-2 text-sm font-medium text-zinc-200">
        <Icon size={15} className="text-zinc-500" /> {title}
      </div>
      {children}
    </div>
  );
}

function ErrorRow({ msg, testid }) {
  return <div className="border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm" data-testid={testid}>{msg}</div>;
}

function EmptyRow({ msg }) {
  return <div className="bg-zinc-950 border border-zinc-900 rounded-md px-4 py-6 text-zinc-600 text-sm">{msg}</div>;
}

// A table that degrades to labeled cards on mobile rather than horizontal
// scroll. `rows[].cells` may contain plain strings or JSX (links, badges).
function Table({ head, rows }) {
  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
      <table className="w-full text-sm hidden md:table">
        <thead className="bg-zinc-900/50 border-b border-zinc-900 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
          <tr>{head.map((h, i) => <th key={i} className="text-left px-4 py-3">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} data-testid={r.testid} className="border-b border-zinc-900 hover:bg-zinc-900/40">
              {r.cells.map((c, i) => <td key={i} className="px-4 py-2 text-zinc-300 align-middle">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="md:hidden divide-y divide-zinc-900">
        {rows.map((r) => (
          <div key={r.key} data-testid={r.testid ? `${r.testid}-card` : undefined} className="p-4 space-y-1.5">
            {r.cells.map((c, i) => (
              <div key={i} className="text-sm text-zinc-300">
                {head[i] && <span className="text-[10px] uppercase tracking-wider font-mono text-zinc-600 mr-1.5">{head[i]}:</span>}
                {c}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, bucket, tone }) {
  const toneCls = tone === "amber" ? "text-amber-300" : tone === "blue" ? "text-blue-300" : "text-zinc-100";
  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{label}</div>
      <div className={`text-2xl font-display mt-1 ${toneCls}`}>{pesoOr(bucket?.amount_minor, "Not available")}</div>
      <div className="text-[11px] text-zinc-500 font-mono mt-1">{bucket?.entries ?? 0} entries · {bucket?.hours ?? 0}h</div>
      {/* PER BUCKET, beside the number it qualifies -- the API returns this
          per bucket for exactly that reason. A window-wide banner cannot tell
          anyone whether THIS total is one of the short ones. */}
      {bucket?.unpriced_entries > 0 && (
        <div className="text-[11px] text-amber-300 mt-1">
          {bucket.unpriced_entries} not counted here, no amount recorded
        </div>
      )}
      {bucket?.unsummable_entries > 0 && (
        <div className="text-[11px] text-red-300 mt-1">
          {bucket.unsummable_entries} not counted here, amount is not a whole number of centavos
        </div>
      )}
    </div>
  );
}
